/**
 * @fileoverview Validation utility functions for form inputs.
 *
 * Provides validation functions for all form fields in the Budget Tracker:
 * - Transaction fields (amount, description, type, categories)
 * - Budget fields (name, start month)
 * - Category fields (name, start date)
 * - Budget template fields (target amount, percentage)
 *
 * @description All validation functions return:
 * - null if validation passes
 * - Error message string if validation fails
 *
 * This consistent return pattern makes it easy to use in forms:
 * const error = validateAmount(value);
 * if (error) showError(error);
 *
 * @module utils/validationUtils
 */

import { formatCurrency } from './moneyUtils';

/**
 * Validate transaction type selection.
 *
 * @param {string} transactionType - Selected transaction type
 * @returns {string|null} Error message or null if valid
 */
const validateTransactionType = (transactionType) => {
    if (!transactionType || transactionType === '') {
        return 'Please select a transaction type';
    }
    return null;
};

/**
 * Validate current user key
 */
const validateCurrentUserKey = (currentUserKey) => {
    if (currentUserKey === null || currentUserKey === undefined || currentUserKey === '') {
        return 'Please select who initiated this transaction';
    }
    return null;
};

/**
 * Validate amount
 */
const validateAmount = (amount) => {
    if (!amount || amount.trim() === '') {
        return 'Amount is required';
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
        return 'Amount must be a positive number';
    }

    if (numAmount > 9999999.99) {
        return `Amount cannot exceed ${formatCurrency(9999999.99)}`;
    }

    return null;
};

/**
 * Validate description
 */
const validateDescription = (description) => {
    if (!description || description.trim() === '') {
        return 'Description is required';
    }

    const trimmed = description.trim();
    if (trimmed.length < 2) {
        return 'Description must be at least 2 characters';
    }

    if (trimmed.length > 100) {
        return 'Description cannot exceed 100 characters';
    }

    return null;
};

/**
 * Validate adjustment categories
 */
const validateAdjustmentCategories = (adjustmentFromCategory, adjustmentToCategory) => {
    const errors = {};

    if (!adjustmentFromCategory || adjustmentFromCategory === '') {
        errors.adjustmentFromCategory = 'Please select a "From" category';
    }

    if (!adjustmentToCategory || adjustmentToCategory === '') {
        errors.adjustmentToCategory = 'Please select a "To" category';
    }

    if (adjustmentFromCategory && adjustmentToCategory && adjustmentFromCategory === adjustmentToCategory) {
        errors.adjustmentCategories = 'From and To categories must be different';
    }

    return errors;
};

/**
 * Validate individual category item
 */
const validateCategoryItem = (catItem, usedCategories, index = null, totalAmount = null, otherCategoriesSum = null) => {
    const catErrors = {};

    // Check if category is selected
    if (!catItem.category || catItem.category === '') {
        catErrors.category = 'Please select a category';
    } else if (usedCategories.has(catItem.category)) {
        catErrors.category = 'Category already selected';
    }

    // Check if amount is valid
    if (!catItem.amount || catItem.amount.trim() === '') {
        catErrors.amount = 'Amount is required';
    } else {
        const parsedAmount = parseFloat(catItem.amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            catErrors.amount = 'Amount must be a positive number';
        } else if (parsedAmount === 0) {
            catErrors.amount = 'Amount cannot be zero';
        }

        // Special validation for first category: check if sum of other categories exceeds total
        if (index === 0 && totalAmount !== null && otherCategoriesSum !== null) {
            if (otherCategoriesSum > totalAmount) {
                catErrors.amount = 'Sum of categories exceeds amount';
            }
        }
    }

    return catErrors;
};

/**
 * Validate category sum matches total amount
 */
const validateCategorySum = (totalCategoryAmount, totalAmount) => {
    const difference = Math.abs(totalCategoryAmount - totalAmount);

    if (difference <= 0.01) {
        return null;
    }

    if (totalCategoryAmount < totalAmount) {
        const remaining = totalAmount - totalCategoryAmount;
        return `Category amounts (${formatCurrency(totalCategoryAmount)}) must equal total amount (${formatCurrency(totalAmount)}). Please categorize the remaining (${formatCurrency(remaining)}).`;
    }

    const overage = totalCategoryAmount - totalAmount;
    return `Category amounts (${formatCurrency(totalCategoryAmount)}) must equal total amount (${formatCurrency(totalAmount)}). Please remove the overage of (${formatCurrency(overage)}).`;
};

/**
 * Validate transaction categories
 */
const validateTransactionCategories = (transactionCategories, amount, transactionType) => {
    const errors = {};
    const categoryErrors = [];
    let totalCategoryAmount = 0;
    const usedCategories = new Set();

    // Calculate sum of other categories (excluding first) for validation
    const otherCategoriesSum = transactionCategories
        .slice(1)
        .reduce((sum, cat) => sum + (parseFloat(cat.amount) || 0), 0);

    const totalAmount = parseFloat(amount);

    transactionCategories.forEach((catItem, index) => {
        const catErrors = validateCategoryItem(
            catItem,
            usedCategories,
            index,
            !isNaN(totalAmount) ? totalAmount : null,
            index === 0 ? otherCategoriesSum : null
        );

        if (catErrors.category === undefined || catErrors.category === 'Category already selected') {
            if (catItem.category) {
                usedCategories.add(catItem.category);
            }
        }

        if (catErrors.amount === undefined && catItem.amount) {
            totalCategoryAmount += parseFloat(catItem.amount);
        }

        if (Object.keys(catErrors).length > 0) {
            categoryErrors[index] = catErrors;
        }
    });

    if (categoryErrors.length > 0) {
        errors.categories = categoryErrors;
    }

    // Check if category amounts sum to total amount (except for Transfer)
    if (transactionType !== 'Transfer' && amount && !isNaN(parseFloat(amount)) && totalCategoryAmount > 0) {
        const sumError = validateCategorySum(totalCategoryAmount, parseFloat(amount));
        if (sumError) {
            errors.categorySum = sumError;
        }
    }

    return errors;
};

/**
 * Validate date
 */
const validateDate = (date) => {
    if (!date) {
        return 'Date is required';
    }

    const selectedDate = new Date(date);
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    if (selectedDate > today) {
        return 'Date cannot be in the future';
    }

    if (selectedDate < oneYearAgo) {
        return 'Date cannot be more than one year ago';
    }

    return null;
};

/**
 * Main validation function that handles all transaction validation
 * This replaces the validateTransaction function in your component
 */
export const validateTransaction = (formData, singleBankMode = false) => {
    const {
        amount,
        description,
        transactionCategories,
        date,
        transactionType,
        currentUserKey,
        adjustmentFromCategory = '',
        adjustmentToCategory = ''
    } = formData;

    const newErrors = {};

    // Transaction type validation
    const typeError = validateTransactionType(transactionType);
    if (typeError) {
        newErrors.transactionType = typeError;
    }

    // User validation - skip in single bank mode and for Adjustments
    if (!singleBankMode && transactionType !== 'Adjustment') {
        const userError = validateCurrentUserKey(currentUserKey);
        if (userError) {
            newErrors.currentUserKey = userError;
        }
    }

    // Amount validation
    const amountError = validateAmount(amount);
    if (amountError) {
        newErrors.amount = amountError;
    }

    // Description validation
    const descError = validateDescription(description);
    if (descError) {
        newErrors.description = descError;
    }

    // Adjustment-specific validation
    if (transactionType === 'Adjustment') {
        const adjErrors = validateAdjustmentCategories(adjustmentFromCategory, adjustmentToCategory);
        Object.assign(newErrors, adjErrors);
    } else {
        // Category validation for non-Adjustment transactions
        const catErrors = validateTransactionCategories(transactionCategories, amount, transactionType);
        Object.assign(newErrors, catErrors);
    }

    // Date validation
    const dateError = validateDate(date);
    if (dateError) {
        newErrors.date = dateError;
    }

    return newErrors;
};

/**
 * Check if validation errors exist
 */
export const hasValidationErrors = (errors) => {
    return Object.keys(errors).length > 0;
};
